import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import HeroCarousel from '../components/HeroCarousel';
import MarketingValueProp from '../components/MarketingValueProp';
import ContentCarousel from '../components/ContentCarousel';
import ContentDetailCarousel from '../components/ContentDetailCarousel';
import ConcertCarousel from '../components/ConcertCarousel';
import DocumentaryCarousel from '../components/DocumentaryCarousel';
import PodcastSection from '../components/PodcastSection';
import DonationBanner from '../components/DonationBanner';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import SearchModal from '../components/SearchModal';
import TestimonialsSection from '../components/TestimonialsSection';
import { concerts } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const [authOpen, setAuthOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [sections, setSections] = useState([]);
  const [sectionContents, setSectionContents] = useState({});
  const [loading, setLoading] = useState(true);

  // Load sections and their contents from backend
  useEffect(() => {
    const loadSectionsAndContents = async () => {
      try {
        // First load active sections
        const sectionsRes = await fetch(`${API_URL}/api/sections/active`);
        const sectionsData = sectionsRes.ok ? await sectionsRes.json() : [];
        
        if (sectionsData.length > 0) {
          setSections(sectionsData);
          
          // Then load contents for each section
          const contentsPromises = sectionsData.map(async (section) => {
            try {
              const res = await fetch(`${API_URL}/api/sections/${section.id}/contents?visible_only=true`);
              const contents = res.ok ? await res.json() : [];
              return { sectionId: section.id, contents };
            } catch {
              return { sectionId: section.id, contents: [] };
            }
          });
          
          const contentsResults = await Promise.all(contentsPromises);
          const contentsMap = {};
          contentsResults.forEach(({ sectionId, contents }) => {
            contentsMap[sectionId] = contents;
          });
          setSectionContents(contentsMap);
        }
      } catch (err) {
        console.error('Error loading sections:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadSectionsAndContents();
  }, []);

  // Helper function to check if a content's date has passed (should move to past events)
  const isContentPast = (content) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // For live content, check live_date
    if (content.is_live && content.live_date) {
      const liveDate = new Date(content.live_date);
      liveDate.setHours(0, 0, 0, 0);
      return liveDate < today;
    }
    
    // For encore content, check encore_end_date
    if (content.is_encore && content.encore_end_date) {
      const encoreEnd = new Date(content.encore_end_date);
      encoreEnd.setHours(0, 0, 0, 0);
      return encoreEnd < today;
    }
    
    return false;
  };

  // Transform backend content to carousel format
  const transformContentForCarousel = (contents, sectionName = '') => {
    // Filter out past events (directos and encores with expired dates)
    const activeContents = contents.filter(content => !isContentPast(content));
    
    return activeContents.map(content => ({
      id: content.id,
      title: content.title,
      composer: content.composer,
      director: content.composer, // For documentaries, composer is often the director
      // Use hero_image or image, with fallback
      image: content.hero_image || content.image || content.heroImage || 'https://images.unsplash.com/photo-1507838153414-b4b713384a76?w=600&q=80',
      heroImage: content.hero_image || content.heroImage || content.image,
      status: content.status || 'available',
      date: content.premiere_date || content.premiereDate,
      releaseLabel: content.release_label || 'DIGITAL PREMIERE',
      artists: content.theater || content.product_info?.theater_name || content.productInfo?.theater_name || '',
      duration: content.product_info?.duration || content.productInfo?.duration || '',
      cast: content.cast || [],
      category: content.category,
      sectionName: sectionName, // Pass section name for badge
      cta: 'CONTRATAR',
      // Live streaming fields
      isLive: content.is_live || false,
      liveDate: content.live_date || '',
      liveTime: content.live_time || '',
      // Encore fields
      isEncore: content.is_encore || false,
      encoreStartDate: content.encore_start_date || '',
      encoreEndDate: content.encore_end_date || ''
    }));
  };

  // Render section based on type
  const renderSection = (section) => {
    const contents = sectionContents[section.id] || [];
    
    // Skip sections with no contents
    if (contents.length === 0) return null;
    
    switch (section.type) {
      case 'content_carousel':
        return (
          <ContentCarousel 
            key={section.id} 
            title={section.name} 
            items={transformContentForCarousel(contents, section.name)} 
          />
        );
      case 'detail_carousel':
        // Use DocumentaryCarousel for documentaries and movies
        return (
          <DocumentaryCarousel 
            key={section.id}
            title={section.name}
            items={transformContentForCarousel(contents, section.name)}
          />
        );
      case 'concert_carousel':
        return (
          <ConcertCarousel 
            key={section.id} 
            title={section.name} 
            concerts={transformContentForCarousel(contents, section.name)} 
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <Header 
        onOpenAuth={() => setAuthOpen(true)} 
        onOpenSearch={() => setSearchOpen(true)}
      />

      {/* Hero Section */}
      <HeroCarousel />

      {/* Marketing Value Proposition Section */}
      <MarketingValueProp />

      {/* Dynamic Sections from Admin */}
      {!loading && sections
        .sort((a, b) => a.order - b.order)
        .map((section) => (
          <div key={section.id} id={`section-${section.id}`} className="scroll-mt-20">
            {renderSection(section)}
          </div>
        ))}

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Podcasts */}
      <PodcastSection />

      {/* Donation Banner */}
      <DonationBanner />

      {/* Footer */}
      <Footer />

      {/* Modals */}
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};

export default HomePage;
